if (!customElements.get('bf-room-carousel')) {
  class BfRoomCarousel extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;

      this.viewport = this.querySelector('[data-bf-room-viewport]');
      this.track = this.querySelector('[data-bf-room-track]');
      this.previousButton = this.querySelector('[data-bf-room-prev]');
      this.nextButton = this.querySelector('[data-bf-room-next]');
      this.originalSlides = Array.from(this.track?.querySelectorAll('[data-bf-room-slide]') || []);

      if (!this.viewport || !this.track || this.originalSlides.length < 2) return;

      this.dataset.initialized = 'true';
      this.originalCount = this.originalSlides.length;
      this.currentIndex = this.originalCount;
      this.isAnimating = false;
      this.isDragging = false;
      this.dragStartX = 0;
      this.dragOffset = 0;
      this.suppressClick = false;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.addClones();
      this.bindEvents();
      this.resizeObserver = new ResizeObserver(() => this.measure(true));
      this.resizeObserver.observe(this.viewport);
      this.measure(true);
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
    }

    addClones() {
      const before = document.createDocumentFragment();
      const after = document.createDocumentFragment();

      this.originalSlides.forEach((slide) => {
        before.appendChild(this.createClone(slide));
        after.appendChild(this.createClone(slide));
      });

      this.track.prepend(before);
      this.track.append(after);
    }

    createClone(slide) {
      const clone = slide.cloneNode(true);
      clone.setAttribute('data-bf-room-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('id');
      clone.removeAttribute('data-shopify-editor-block');
      clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
        element.setAttribute('tabindex', '-1');
      });
      return clone;
    }

    bindEvents() {
      this.previousButton?.addEventListener('click', () => this.move(-1));
      this.nextButton?.addEventListener('click', () => this.move(1));
      this.track.addEventListener('transitionend', () => this.normalizeIndex());

      this.viewport.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.move(-1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.move(1);
        }
      });

      this.viewport.addEventListener('pointerdown', (event) => this.startDrag(event));
      this.viewport.addEventListener('pointermove', (event) => this.drag(event));
      this.viewport.addEventListener('pointerup', (event) => this.endDrag(event));
      this.viewport.addEventListener('pointercancel', (event) => this.endDrag(event));
      this.viewport.addEventListener('dragstart', (event) => event.preventDefault());
      this.viewport.addEventListener('click', (event) => {
        if (!this.suppressClick) return;
        event.preventDefault();
        event.stopPropagation();
        this.suppressClick = false;
      }, true);
    }

    measure(withoutAnimation = false) {
      const slide = this.track.querySelector('[data-bf-room-slide]');
      if (!slide) return;

      const styles = getComputedStyle(this.track);
      const gap = parseFloat(styles.columnGap || styles.gap) || 0;
      this.step = slide.getBoundingClientRect().width + gap;
      this.render(withoutAnimation);
    }

    move(direction) {
      if (this.isAnimating || !this.step) return;
      this.isAnimating = true;
      this.currentIndex += direction;
      this.render(false);
      if (this.reducedMotion) this.normalizeIndex();
    }

    render(withoutAnimation = false, offset = 0) {
      this.track.style.transition = withoutAnimation || this.reducedMotion
        ? 'none'
        : 'transform 420ms cubic-bezier(.22, 1, .36, 1)';
      this.track.style.transform = `translate3d(${(-this.currentIndex * this.step) + offset}px, 0, 0)`;
    }

    normalizeIndex() {
      this.isAnimating = false;

      if (this.currentIndex >= this.originalCount * 2) {
        this.currentIndex -= this.originalCount;
        this.render(true);
      } else if (this.currentIndex < this.originalCount) {
        this.currentIndex += this.originalCount;
        this.render(true);
      }
    }

    startDrag(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (this.isAnimating) return;

      this.isDragging = true;
      this.dragStartX = event.clientX;
      this.dragOffset = 0;
      this.track.style.transition = 'none';
      this.viewport.setPointerCapture?.(event.pointerId);
    }

    drag(event) {
      if (!this.isDragging) return;
      this.dragOffset = event.clientX - this.dragStartX;
      if (Math.abs(this.dragOffset) > 8) this.suppressClick = true;
      this.render(true, this.dragOffset);
    }

    endDrag(event) {
      if (!this.isDragging) return;

      this.isDragging = false;
      this.viewport.releasePointerCapture?.(event.pointerId);
      const threshold = Math.min(this.step * .18, 72);

      if (Math.abs(this.dragOffset) > threshold) {
        this.currentIndex += this.dragOffset < 0 ? 1 : -1;
        this.isAnimating = true;
      }

      this.dragOffset = 0;
      this.render(false);
      if (this.reducedMotion) this.normalizeIndex();
      window.setTimeout(() => {
        this.suppressClick = false;
      }, 120);
    }
  }

  customElements.define('bf-room-carousel', BfRoomCarousel);
}
